// Dedicated Admin page for delivery-partner approvals & management.
// /admin/delivery-partners — always visible, live-refresh, one-tap approve/reject/suspend/reactivate.
import React, { useCallback, useEffect, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { RefreshCw, CheckCircle, XCircle, Bike, Phone, MapPin, Star } from 'lucide-react';
import api from '../../utils/api';

const FILTERS = [
  { key: 'pending',   label: '⏳ Pending' },
  { key: 'active',    label: '🟢 Active' },
  { key: 'suspended', label: '⚠️ Suspended' },
  { key: 'rejected',  label: '❌ Rejected' },
  { key: '',          label: 'All' },
];

export default function AdminDeliveryPartners() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [lightbox, setLightbox] = useState({ open: false, images: [], idx: 0 });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/delivery/partners${filter ? `?status=${filter}` : ''}`);
      setRows(r.data.partners || []); setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setBusy(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (id, action, name) => {
    let reason = null;
    if (action === 'reject') {
      reason = window.prompt(`Reject "${name}"?\n\nPlease provide a rejection reason (this will be sent to the applicant):`, '');
      if (reason === null) return; // cancelled
      if (!reason.trim()) { toast.error('Rejection reason required'); return; }
    } else if (action === 'suspend') {
      reason = window.prompt(`Suspend "${name}"? Reason (optional, shown to partner):`, '') || '';
      if (reason === null) return;
    } else {
      const confirmMsg = {
        approve: `Approve "${name}" as delivery partner?`,
        reactivate: `Reactivate "${name}"?`,
      }[action];
      if (!window.confirm(confirmMsg)) return;
    }
    setBusyIds((p) => ({ ...p, [id]: true }));
    try {
      const params = new URLSearchParams({ action });
      if (reason) params.append('reason', reason);
      await api.post(`/admin/delivery/partner/${id}/action?${params.toString()}`, {});
      toast.success(`✅ ${name} ${action}d${reason ? ' · reason sent' : ''}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally { setBusyIds((p) => ({ ...p, [id]: false })); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white p-4 sm:p-6" data-testid="admin-delivery-partners-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent flex items-center gap-2">
              <Bike size={28} /> Delivery Partners
            </h1>
            <div className="text-[11px] opacity-70 mt-1">Approve, suspend & manage all delivery partners · Auto-refresh 10s</div>
          </div>
          <button onClick={load} disabled={busy} data-testid="admin-partners-refresh"
                  className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black flex items-center gap-1">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <KpiCard label="⏳ Pending" value={counts.pending || 0} color="from-amber-500 to-orange-500" testId="kpi-pending" />
          <KpiCard label="🟢 Active" value={counts.active || 0} color="from-emerald-500 to-cyan-500" testId="kpi-active" />
          <KpiCard label="📡 Online now" value={counts.available || 0} color="from-cyan-500 to-blue-500" testId="kpi-online" />
          <KpiCard label="⚠️ Suspended" value={counts.suspended || 0} color="from-slate-500 to-gray-600" testId="kpi-suspended" />
          <KpiCard label="❌ Rejected" value={counts.rejected || 0} color="from-rose-500 to-red-600" testId="kpi-rejected" />
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 flex-wrap mb-3" data-testid="admin-partners-filters">
          {FILTERS.map((f) => (
            <button key={f.label} onClick={() => setFilter(f.key)} data-testid={`admin-partners-filter-${f.key || 'all'}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-black ${filter === f.key ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
              {f.label} {f.key && counts[f.key] ? `(${counts[f.key]})` : ''}
            </button>
          ))}
        </div>

        {/* Partners list */}
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden" data-testid="admin-partners-list">
          {rows.length === 0 && (
            <div className="p-12 text-center opacity-60 text-sm">
              {busy ? '⏳ Loading…' : filter === 'pending' ? '🎉 No pending applications — sab up-to-date!' : 'No partners in this filter'}
            </div>
          )}
          <div className="divide-y divide-slate-800">
            {rows.map((p) => (
              <div key={p.id} className="p-4 hover:bg-slate-800/40 transition" data-testid={`admin-partner-row-${p.id.slice(0,8)}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  {/* Avatar + Name */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-lg font-black">
                    {p.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-black flex items-center gap-2 flex-wrap">
                      {p.name}
                      <StatusBadge status={p.status} available={p.available} />
                      {p.rating_count > 0 && (
                        <span className="text-[10px] flex items-center gap-0.5 text-yellow-300">
                          <Star size={10} className="fill-yellow-300" /> {Number(p.rating_avg || 0).toFixed(1)} ({p.rating_count})
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] opacity-80 flex items-center gap-3 flex-wrap mt-0.5">
                      <span className="flex items-center gap-1"><Phone size={10} /> <a href={`tel:${p.phone}`} className="hover:text-emerald-300">{p.phone}</a></span>
                      <span className="flex items-center gap-1"><MapPin size={10} /> {p.city}{p.zone ? ` · ${p.zone}` : ''}</span>
                      <span className="uppercase font-black opacity-80">🛵 {p.vehicle_type}{p.vehicle_number ? ` · ${p.vehicle_number}` : ''}</span>
                    </div>
                    <div className="text-[10px] opacity-60 mt-0.5">
                      📦 {p.total_deliveries || 0} deliveries · 💰 {Number(p.total_earned_at || 0).toFixed(0)} AT earned · Applied {new Date(p.created_at || Date.now()).toLocaleDateString('en-IN')}
                      {p.available_hours && <span className="ml-2">· 🕒 {p.available_hours}</span>}
                      {p.notes && <span className="italic ml-2">· "{p.notes.slice(0, 60)}"</span>}
                    </div>
                    {p.id_proof_url && (
                      <button onClick={() => setLightbox({ open: true, images: [p.id_proof_url], idx: 0 })}
                              data-testid={`admin-partner-${p.id.slice(0,8)}-id-proof`}
                              className="inline-flex items-center gap-1 mt-1 text-[10px] text-cyan-300 hover:text-cyan-200 underline">
                        <img src={p.id_proof_url} alt="ID" className="w-8 h-8 rounded object-cover border border-cyan-400/60 cursor-zoom-in" />
                        📄 View ID Proof (zoom)
                      </button>
                    )}
                    {p.welcome_kit_wa_url && p.status === 'active' && (
                      <a href={p.welcome_kit_wa_url} target="_blank" rel="noopener noreferrer"
                         data-testid={`admin-partner-${p.id.slice(0,8)}-wa-kit`}
                         className="inline-flex items-center gap-1 ml-2 mt-1 text-[10px] text-emerald-300 hover:text-emerald-200 underline">
                        📱 Send WhatsApp Welcome Kit
                      </a>
                    )}
                    {p.reject_reason && p.status === 'rejected' && (
                      <div className="text-[10px] text-rose-300 mt-1">❌ Reject reason: {p.reject_reason}</div>
                    )}
                    {p.suspend_reason && (
                      <div className="text-[10px] text-amber-300 mt-1">⚠️ {p.suspend_reason}</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 flex-wrap items-center">
                    {p.status === 'pending' && (
                      <>
                        <button onClick={() => act(p.id, 'approve', p.name)} disabled={busyIds[p.id]}
                                data-testid={`admin-partner-${p.id.slice(0,8)}-approve`}
                                className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-black flex items-center gap-1 disabled:opacity-40 shadow-lg shadow-emerald-500/30">
                          <CheckCircle size={14} /> Approve
                        </button>
                        <button onClick={() => act(p.id, 'reject', p.name)} disabled={busyIds[p.id]}
                                data-testid={`admin-partner-${p.id.slice(0,8)}-reject`}
                                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-black flex items-center gap-1 disabled:opacity-40">
                          <XCircle size={14} /> Reject
                        </button>
                      </>
                    )}
                    {p.status === 'active' && (
                      <button onClick={() => act(p.id, 'suspend', p.name)} disabled={busyIds[p.id]}
                              data-testid={`admin-partner-${p.id.slice(0,8)}-suspend`}
                              className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-black disabled:opacity-40">
                        ⚠️ Suspend
                      </button>
                    )}
                    {p.status === 'suspended' && (
                      <button onClick={() => act(p.id, 'reactivate', p.name)} disabled={busyIds[p.id]}
                              data-testid={`admin-partner-${p.id.slice(0,8)}-reactivate`}
                              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-black disabled:opacity-40">
                        ✅ Reactivate
                      </button>
                    )}
                    {p.status === 'rejected' && (
                      <button onClick={() => act(p.id, 'approve', p.name)} disabled={busyIds[p.id]}
                              data-testid={`admin-partner-${p.id.slice(0,8)}-approve-rej`}
                              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs font-black disabled:opacity-40">
                        ↻ Approve Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {lightbox.open && (
        <Lightbox images={lightbox.images} startIndex={lightbox.idx}
                  onClose={() => setLightbox({ open: false, images: [], idx: 0 })} />
      )}
    </div>
  );
}

function KpiCard({ label, value, color, testId }) {
  return (
    <div className={`rounded-xl bg-gradient-to-br ${color} p-3 text-center shadow-lg`} data-testid={testId}>
      <div className="text-[10px] opacity-90 font-black uppercase">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status, available }) {
  const map = {
    pending: 'bg-amber-500/25 text-amber-300 border-amber-400/60',
    active: 'bg-emerald-500/25 text-emerald-300 border-emerald-400/60',
    suspended: 'bg-slate-500/25 text-slate-300 border-slate-400/60',
    rejected: 'bg-rose-500/25 text-rose-300 border-rose-400/60',
  };
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border font-black uppercase ${map[status] || 'bg-slate-600'}`}>
      {status}{status === 'active' ? (available ? ' · 🟢' : ' · ⚫') : ''}
    </span>
  );
}
